import { ServiceError } from '../errors'
import { readLocal } from '../local-store'
import { simulate } from '../mock-utils'
import { money } from '../types'
import { pagosMock } from '../pagos/mock'
import { changeResult, confirmWithPayment, pendingChange, revertChangePayment, waitForChangePayment } from './change-payment'
import type { ChangePaymentBackend } from './change-payment'
import { mockApply, mockAvailability, mockCancel, mockCancelQuote, mockChangeOptions, mockQuote, mockRequestChange, mockRevert, mockTimeChoices, mockUnitChoices, withMockChanges } from './mock-changes'
import { derivePhase } from './phase'
import type { Dispositive, Phase, RegistrationCatalogs, RegistrationRules, ReservaService, Stay, Traveler, TravelerDetails } from './types'

/**
 * Datos de ejemplo del diseño (inventario D4): Marta Ruiz, Apartamentos Centro Histórico, Ático Plaza
 * Mayor, 3–6 oct 2026, cerradura conectada, fianza 300 €, tres ocupantes (Jorge sin registrar).
 * Códigos especiales para probar estados:
 *  - `caducado` → enlace caducado · `invalido` → enlace inválido · `hotel` → Hotel Mirador del Puerto
 *  - `fase-before|arrival|during|checkout|after` → fuerza la fase (el registro y la llave se ajustan a ella)
 *  - `cancelada` → reserva cancelada
 *  - Registro (8.2): `titular-pendiente` (nadie registrado), `completar` (Ana con datos y firma pendientes),
 *    `sin-selfie` (la propiedad no pide la cara), `forzar-escaneo` (sin "Rellenar a mano")
 *  - `error-*`, `offline-*`, `lento-*` → ver services/mock-utils.ts
 */
const details = (d: Partial<TravelerDetails> & Pick<TravelerDetails, 'name' | 'surname1' | 'surname2'>): TravelerDetails => ({
  email: null,
  bornDate: null,
  identifier: null,
  identifierTypeId: null,
  sexId: null,
  countryId: null,
  provinceId: null,
  municipalityId: null,
  city: null,
  address: null,
  postalCode: null,
  supportNumber: null,
  relationship: null,
  ...d,
})

/** Ids reales de los tipos de documento del PMS (app actual, IdentifierTypeId). */
export const ID_TYPE = { dni: '6D86C358DF454704ADF6AF98A8B3135D', passport: '233092EB24424B98928696BE268640C5', residence: '6553EE440E7542669B54B596B19B2D9F' } as const

export const MOCK_CATALOGS: RegistrationCatalogs = {
  documentCountries: [
    {
      id: 'ES',
      name: 'España',
      isoCountry: 'ES',
      isDefault: true,
      flagUrl: null,
      documentTypes: [
        { id: 'DT1', name: 'DOCUMENTO_IDENTIDAD', label: 'DNI', scanType: 'DNI', front: false, back: false, both: true, selfie: true, scanCode: 'ESP' },
        { id: 'DT2', name: 'PASAPORTE', label: 'Pasaporte', scanType: 'PASAPORTE', front: true, back: false, both: false, selfie: true, scanCode: 'PASSPORT' },
        { id: 'DT3', name: 'DOCUMENTO_IDENTIDAD', label: 'NIE / tarjeta de residencia', scanType: 'DNI', front: false, back: false, both: true, selfie: true, scanCode: 'ESP_NIE' },
        { id: 'DT4', name: 'CARNET_DE_CONDUCIR', label: 'Carné de conducir (UE)', scanType: 'DNI', front: false, back: false, both: true, selfie: true, scanCode: 'ESP_DRIVING' },
        { id: 'DT5', name: 'qr_app_midni', label: 'qr_app_midni', scanType: 'qr', front: false, back: false, both: false, selfie: false, scanCode: null },
      ],
    },
    {
      id: 'FR',
      name: 'Francia',
      isoCountry: 'FR',
      isDefault: false,
      flagUrl: null,
      documentTypes: [
        { id: 'DT6', name: 'DOCUMENTO_IDENTIDAD', label: "Carte d'identité", scanType: 'DNI', front: false, back: false, both: true, selfie: true, scanCode: 'FRA' },
        { id: 'DT7', name: 'PASAPORTE', label: 'Pasaporte', scanType: 'PASAPORTE', front: true, back: false, both: false, selfie: true, scanCode: 'PASSPORT' },
      ],
    },
  ],
  countries: [
    { id: 'ES', name: 'España', value: 'ES' },
    { id: 'FR', name: 'Francia', value: 'FR' },
    { id: 'PT', name: 'Portugal', value: 'PT' },
    { id: 'IT', name: 'Italia', value: 'IT' },
    { id: 'GB', name: 'Reino Unido', value: 'GB' },
  ],
  provinces: [
    { id: 'P08', name: 'Barcelona', value: 'Barcelona' },
    { id: 'P04', name: 'Almería', value: 'Almería' },
    { id: 'P28', name: 'Madrid', value: 'Madrid' },
    { id: 'P46', name: 'Valencia', value: 'Valencia' },
  ],
  sexes: [
    { id: 'S1', name: 'Mujer', value: 'mujer' },
    { id: 'S2', name: 'Hombre', value: 'hombre' },
  ],
  identifierTypes: [
    { id: ID_TYPE.dni, name: 'Documento de identidad', value: 'Documento de identidad' },
    { id: ID_TYPE.passport, name: 'Pasaporte', value: 'Pasaporte' },
    { id: ID_TYPE.residence, name: 'Permiso de residencia', value: 'Permiso de residencia' },
  ],
  relationships: [
    { id: 'R1', name: 'Hijo o hija', value: 'hijo', type: 'menor', optionA: 'R4,R5,R6' },
    { id: 'R2', name: 'Sobrino o sobrina', value: 'sobrino', type: 'menor', optionA: 'R7' },
    { id: 'R3', name: 'Otro', value: 'otro', type: 'otro', optionA: 'R6' },
    { id: 'R4', name: 'Padre', value: 'padre', type: 'adulto', optionA: '' },
    { id: 'R5', name: 'Madre', value: 'madre', type: 'adulto', optionA: '' },
    { id: 'R6', name: 'Tutor legal', value: 'tutor', type: 'adulto', optionA: '' },
    { id: 'R7', name: 'Tío o tía', value: 'tio', type: 'adulto', optionA: '' },
  ],
}

export const MOCK_RULES: RegistrationRules = { forceScan: false, forceSelfie: true, allowFaceMatchSkip: true, ministerioInterior: true }

const MARTA = details({ name: 'Marta', surname1: 'Ruiz', surname2: 'García', email: 'marta.ruiz@gmail.com', bornDate: '1996-04-12', identifier: '12345678Z', identifierTypeId: ID_TYPE.dni, sexId: 'S1', countryId: 'ES', provinceId: 'P08', municipalityId: 'M080193', address: 'Calle Aragó 120, 3.º 2.ª', postalCode: '08015', supportNumber: 'CAA123456' })
const ANA = details({ name: 'Ana', surname1: 'Serrano', surname2: 'Vidal', email: 'ana.serrano@gmail.com', bornDate: '1995-09-02', identifier: '87654321X', identifierTypeId: ID_TYPE.dni, sexId: 'S1', countryId: 'ES', provinceId: 'P28', municipalityId: 'M280796', address: 'Calle de Toledo 45, 2.º', postalCode: '28005', supportNumber: 'CBB654321' })

const TRAVELERS: Traveler[] = [
  { id: 'T1', name: 'Marta', surname: 'Ruiz García', isAdult: true, canDelete: false, missingFields: [], signaturePending: false, details: MARTA },
  { id: 'T2', name: 'Ana', surname: 'Serrano Vidal', isAdult: true, canDelete: true, missingFields: [], signaturePending: false, details: ANA },
]

const DISPOSITIVES: Dispositive[] = [
  { id: 'D1', name: 'Portal', type: 'portal', commands: [{ id: 'C1', name: 'Abrir portal', type: 'abrir', serverPath: 'pruebas2.availroom.com', messageBeforeUse: null }], intercom: null, text: null, title: null },
  { id: 'D2', name: 'Puerta del ático', type: 'puerta', commands: [{ id: 'C2', name: 'Abrir puerta', type: 'abrir', serverPath: 'pruebas2.availroom.com', messageBeforeUse: null }], intercom: null, text: null, title: null },
  { id: 'D3', name: 'Garaje', type: 'garaje', commands: [{ id: 'C3', name: 'Abrir garaje', type: 'abrir', serverPath: 'pruebas2.availroom.com', messageBeforeUse: null }], intercom: null, text: null, title: null },
  {
    id: 'D4',
    name: 'Salón',
    type: 'aire_acondicionado',
    commands: [
      { id: 'C4', name: 'frio', type: 'frio', serverPath: 'pruebas2.availroom.com', messageBeforeUse: null },
      { id: 'C5', name: 'calor', type: 'calor', serverPath: 'pruebas2.availroom.com', messageBeforeUse: null },
      { id: 'C6', name: 'temperatura', type: 'temperatura', serverPath: 'pruebas2.availroom.com', messageBeforeUse: null },
      { id: 'C7', name: 'apagado', type: 'apagado', serverPath: 'pruebas2.availroom.com', messageBeforeUse: null },
    ],
    intercom: null,
    text: null,
    title: null,
  },
]

export const MOCK_STAY: Stay = {
  id: 'R-48213',
  guestCode: 'demo',
  serverPath: 'pruebas2.availroom.com',
  reference: 'ACH-48213',
  property: { name: 'Apartamentos Centro Histórico', primaryColor: '#5C6B24', logoUrl: null, coverImageUrl: null, timeZone: 'Europe/Madrid' },
  unit: { roomId: 'U-ATICO', name: 'Ático Plaza Mayor', address: 'Plaza Mayor 4, 4.º, 28012 Madrid', street: 'Plaza Mayor 4, 4.º', locality: '28012 Madrid', lat: 40.4155, lng: -3.7074, photos: [], keyType: 'conectada' },
  dates: { checkIn: '2026-10-03T16:00:00+02:00', checkOut: '2026-10-06T11:00:00+02:00', nights: 3 },
  guests: { adults: 3, children: 0, pendingToRegister: 1 },
  registration: { total: 3, pending: 1, holderPending: false, missingData: false, dataError: false, travelers: TRAVELERS, rules: MOCK_RULES, catalogs: MOCK_CATALOGS },
  documents: [{ name: 'Contrato de alojamiento', url: 'https://example.invalid/contrato.pdf', type: 'PDF', travelerId: null }],
  dispositives: DISPOSITIVES,
  guide: {
    conditions: [{ name: 'Normas de la casa', description: 'Silencio de 22:00 a 8:00. No se puede fumar dentro ni en la terraza. Máximo 3 personas.', iconUrl: null, images: [], videos: [] }],
    manual: [
      { name: 'Cómo entrar', description: 'Portal marrón junto a la farmacia. Ascensor a la 4.ª planta; la puerta del ático es la única del rellano.', iconUrl: null, images: [], videos: [] },
      { name: 'Climatización', description: 'El mando está en la pared del salón. Modo frío o calor con el botón azul/rojo. Recomendamos 22 °C.', iconUrl: null, images: [], videos: [] },
      { name: 'Lavadora y secadora', description: 'En el baño. Programa corto: botón «30 min». El detergente está bajo el lavabo.', iconUrl: null, images: [], videos: [] },
      { name: 'Basura y reciclaje', description: 'Bolsa amarilla: envases. Azul: papel. Orgánico: contenedor marrón en la esquina de Toledo con Imperial.', iconUrl: null, images: [], videos: [] },
    ],
    phones: [{ name: 'Recepción', number: '+34 600 123 456' }],
  },
  bills: [],
  channel: { name: 'Booking.com' },
  phase: 'before',
  contact: { whatsapp: '34600123456', supportPhone: '+34 600 123 456', emergencyPhone: '+34 600 123 456' },
  wifi: { ssid: 'AticoPlazaMayor', password: 'bienvenido2026' },
  currency: 'EUR',
  totalPrice: money(45000),
  flags: {
    autoCheckIn: true,
    showDomotic: false,
    showGetExtras: true,
    showGetBills: true,
    allowGetBill: true,
    showGetNewReserve: true,
    allowModify: true,
    allowCancel: true,
    paidWithGateway: true,
    cardChannel: false,
    previousPaidOnCard: false,
    cancelled: false,
  },
  environmentId: 'DEMO',
  partner: { civitatisId: null, provinceId: null },
}

export const MOCK_STAY_HOTEL: Stay = {
  ...MOCK_STAY,
  id: 'R-30412',
  reference: 'HMP-30412',
  property: { name: 'Hotel Mirador del Puerto', primaryColor: '#1F3A63', logoUrl: null, coverImageUrl: null, timeZone: 'Europe/Madrid' },
  unit: { roomId: 'U-304', name: 'Habitación 304 · Doble superior', address: 'Paseo del Puerto 12, 04001 Almería', street: 'Paseo del Puerto 12', locality: '04001 Almería', lat: 36.8381, lng: -2.4597, photos: [], keyType: null },
  guests: { adults: 2, children: 0, pendingToRegister: 2 },
  registration: { total: 2, pending: 2, holderPending: true, missingData: false, dataError: false, travelers: [], rules: { ...MOCK_RULES, forceSelfie: false }, catalogs: MOCK_CATALOGS },
  documents: [],
  dispositives: [],
  guide: {
    conditions: [{ name: 'Normas del hotel', description: 'Desayuno de 7:30 a 10:30. Piscina de 10:00 a 20:00.', iconUrl: null, images: [], videos: [] }],
    manual: [
      { name: 'Climatización', description: 'La habitación 304 se climatiza con mando. El mando está en la mesilla.', iconUrl: null, images: [], videos: [] },
      { name: 'Caja fuerte', description: 'Dentro del armario. Cierra con un código de 4 cifras.', iconUrl: null, images: [], videos: [] },
    ],
    phones: [{ name: 'Recepción 24 h', number: '+34 950 123 456' }],
  },
  channel: { name: 'Booking.com' },
  contact: { whatsapp: '34950123456', supportPhone: '+34 950 123 456', emergencyPhone: '+34 950 123 456' },
  wifi: { ssid: 'MiradorGuest', password: 'puerto304' },
  totalPrice: money(55500),
  flags: { ...MOCK_STAY.flags, showDomotic: false, allowModify: false },
}

/** Variantes del registro por código (8.2). */
function forRegistration(stay: Stay, guestCode: string): Stay {
  let reg = stay.registration
  if (guestCode.includes('titular-pendiente')) reg = { ...reg, pending: reg.total, holderPending: true, travelers: [] }
  if (guestCode.includes('completar')) {
    reg = {
      ...reg,
      missingData: true,
      travelers: reg.travelers.map((tr) => (tr.id === 'T2' ? { ...tr, missingFields: ['postalCode', 'address', 'signature'], signaturePending: true, details: { ...tr.details, postalCode: null, address: null } } : tr)),
    }
  }
  if (guestCode.includes('sin-selfie')) reg = { ...reg, rules: { ...reg.rules, forceSelfie: false } }
  if (guestCode.includes('forzar-escaneo')) reg = { ...reg, rules: { ...reg.rules, forceScan: true } }
  // Registros guardados con el mock del check-in en este dispositivo (services/checkin/mock.ts).
  const saved = readLocal<{ id: string; name: string; surname: string; isAdult: boolean; isHolder: boolean; email: string }[]>(guestCode, 'mockRegistered') ?? []
  if (saved.length > 0) {
    const added: Traveler[] = saved.map((r) => ({ id: r.id, name: r.name, surname: r.surname, isAdult: r.isAdult, canDelete: !r.isHolder, missingFields: [], signaturePending: false, details: details({ name: r.name, surname1: r.surname.split(' ')[0] ?? '', surname2: r.surname.split(' ').slice(1).join(' '), email: r.email || null }) }))
    reg = { ...reg, travelers: [...reg.travelers, ...added], pending: Math.max(0, reg.pending - saved.length), holderPending: reg.holderPending && !saved.some((r) => r.isHolder) }
  }
  return reg === stay.registration ? stay : { ...stay, registration: reg, guests: { ...stay.guests, pendingToRegister: reg.pending } }
}

/** El registro y la llave del mock se ajustan a la fase para reproducir el bundle (Inicio.dc.html). */
function forPhase(base: Stay, phase: Phase): Stay {
  if (phase === 'before' || phase === 'cancelled') return base
  const isDemo = base.id === MOCK_STAY.id
  const travelers = isDemo ? [...TRAVELERS, { id: 'T3', name: 'Jorge', surname: 'Molina Paz', isAdult: true, canDelete: true, missingFields: [], signaturePending: false, details: details({ name: 'Jorge', surname1: 'Molina', surname2: 'Paz' }) }] : base.registration.travelers
  return {
    ...base,
    guests: { ...base.guests, pendingToRegister: 0 },
    registration: { ...base.registration, pending: 0, holderPending: false, missingData: false, travelers },
    bills: phase === 'after' ? [{ id: 'F-2026-0412', url: 'https://example.invalid/factura-2026-0412.pdf' }] : base.bills,
    flags: { ...base.flags, showDomotic: isDemo && phase !== 'after' },
  }
}

export const reservaMock: ReservaService = {
  async getStay(guestCode, _lang, signal) {
    await simulate(guestCode, 'reserva', signal)
    if (guestCode === 'caducado') throw new ServiceError('link_expired')
    if (guestCode === 'invalido' || guestCode === '') throw new ServiceError('link_invalid')
    // Cambios y cancelación hechos en este dispositivo con el mock (mock-changes.ts).
    const base = withMockChanges(guestCode.includes('hotel') ? MOCK_STAY_HOTEL : MOCK_STAY, guestCode)
    const forced = /fase-(before|arrival|during|checkout|after)/.exec(guestCode)?.[1] as Phase | undefined
    const cancelled = guestCode.includes('cancelada') || base.flags.cancelled
    const phase = cancelled ? 'cancelled' : (forced ?? derivePhase(base.dates.checkIn, base.dates.checkOut))
    const stay = forRegistration(forPhase(base, phase), guestCode)
    return { ...stay, guestCode, phase, flags: { ...stay.flags, cancelled } }
  },

  // 8.5 · Reserva y cambios (bloque 5): simulación completa en mock-changes.ts.
  async getChangeOptions(stay, _lang, signal) {
    await simulate(stay.guestCode, 'reserva', signal)
    return mockChangeOptions(stay, stay.guestCode)
  },
  async getAvailability(stay, from, to, signal) {
    await simulate(stay.guestCode, 'reserva', signal)
    return mockAvailability(stay, stay.guestCode, from, to)
  },
  async getUnitChoices(stay, signal) {
    await simulate(stay.guestCode, 'reserva', signal)
    return mockUnitChoices(stay)
  },
  async getTimeChoices(stay, type, signal) {
    await simulate(stay.guestCode, 'reserva', signal)
    return mockTimeChoices(type)
  },
  async quoteChange(stay, input, _lang, signal) {
    await simulate(stay.guestCode, 'reserva', signal)
    return mockQuote(stay, stay.guestCode, input)
  },
  // Mismo orden que la app actual y mismas mitigaciones que real (D45), con el banco simulado del bloque 3.
  confirmChange: (stay, quote, card, lang) => confirmWithPayment(mockBackend, stay, quote, card, lang),
  waitForChange: (stay, changeId, options) => waitForChangePayment(mockBackend, stay, changeId, options),
  getChangeResult: (stay, changeId, lang) => changeResult(mockBackend, stay, changeId, lang),
  revertChange: (stay, changeId, lang) => revertChangePayment(mockBackend, stay, changeId, lang),
  getPendingChange: (stay, lang, signal) => pendingChange(mockBackend, stay, lang, signal),
  async quoteCancel(stay, _lang, signal) {
    await simulate(stay.guestCode, 'reserva', signal)
    return mockCancelQuote(stay, stay.guestCode)
  },
  cancel: (stay) => mockCancel(stay),
  requestChange: (stay, input) => mockRequestChange(stay, input),
}

const mockBackend: ChangePaymentBackend = {
  pagos: pagosMock,
  apply: (stay, quote, lang) => mockApply(stay, quote, (g) => reservaMock.getStay(g, lang)),
  revert: (stay, _pending, lang) => mockRevert(stay, (g) => reservaMock.getStay(g, lang)),
  getStay: (guestCode, lang) => reservaMock.getStay(guestCode, lang),
}
